import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Wallet, Coins, TrendingUp, Lock, Unlock, ExternalLink, Copy, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = () => {
  const { user, token, updateUser } = useAuth();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/token/info`).then(r => setTokenInfo(r.data)).catch(() => {});
    axios.get(`${API}/wallet/addresses`).then(r => setAddresses(r.data.addresses)).catch(() => {});
  }, []);

  const handleStake = async () => {
    const amt = parseFloat(stakeAmount);
    if (!amt || amt <= 0) return toast.error('Enter valid amount');
    if (amt > (user?.aih_tokens || 0)) return toast.error('Insufficient tokens');
    setIsStaking(true);
    try {
      await axios.post(`${API}/wallet/stake?amount=${amt}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Staked ${amt} AIH!`);
      updateUser({ aih_tokens: (user.aih_tokens || 0) - amt, staked_tokens: (user.staked_tokens || 0) + amt });
      setStakeAmount('');
    } catch (e) { toast.error('Failed to stake'); }
    setIsStaking(false);
  };

  const handleUnstake = async () => {
    const amt = parseFloat(unstakeAmount);
    if (!amt || amt <= 0) return toast.error('Enter valid amount');
    if (amt > (user?.staked_tokens || 0)) return toast.error('Insufficient staked');
    setIsUnstaking(true);
    try {
      await axios.post(`${API}/wallet/unstake?amount=${amt}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Unstaked ${amt} AIH!`);
      updateUser({ aih_tokens: (user.aih_tokens || 0) + amt, staked_tokens: (user.staked_tokens || 0) - amt });
      setUnstakeAmount('');
    } catch (e) { toast.error('Failed to unstake'); }
    setIsUnstaking(false);
  };

  const copyAddress = (addr, index) => {
    navigator.clipboard.writeText(addr);
    setCopiedAddr(index);
    toast.success('Address copied!');
    setTimeout(() => setCopiedAddr(null), 2000);
  };

  const price = tokenInfo?.price || 0.0813;
  const total = ((user?.aih_tokens || 0) + (user?.staked_tokens || 0)) * price;

  const networkColors = {
    eth: 'text-blue-400',
    btc: 'text-[#F7931A]',
    trx: 'text-red-400',
    polygon: 'text-purple-400',
    zksync: 'text-blue-300',
    arbitrum: 'text-blue-500'
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="wallet-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Wallet className="text-[#D4AF37]" /> AIH Wallet
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60 text-sm">Available</span>
              <Coins className="text-[#D4AF37]" size={20} />
            </div>
            <p className="font-unbounded text-3xl font-bold text-white mb-1">{(user?.aih_tokens || 0).toLocaleString()}</p>
            <p className="text-sm text-[#D4AF37]">AIH Tokens</p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60 text-sm">Staked</span>
              <Lock className="text-purple-400" size={20} />
            </div>
            <p className="font-unbounded text-3xl font-bold text-white mb-1">{(user?.staked_tokens || 0).toLocaleString()}</p>
            <p className="text-sm text-purple-400">Staked AIH</p>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white/60 text-sm">Portfolio</span>
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <p className="font-unbounded text-3xl font-bold text-white mb-1">${total.toFixed(2)}</p>
            <p className="text-sm text-green-400">USD Value</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="glass-panel p-6">
            <Tabs defaultValue="stake">
              <TabsList className="grid w-full grid-cols-2 bg-[#0A1628]">
                <TabsTrigger value="stake" data-testid="stake-tab">Stake</TabsTrigger>
                <TabsTrigger value="unstake" data-testid="unstake-tab">Unstake</TabsTrigger>
              </TabsList>
              <TabsContent value="stake" className="mt-6 space-y-4">
                <div>
                  <Label className="text-white/80">Amount to Stake</Label>
                  <Input type="number" placeholder="0.00" value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)} className="input-cosmic" data-testid="stake-amount" />
                  <p className="text-xs text-white/40 mt-1">Available: {(user?.aih_tokens || 0).toLocaleString()} AIH</p>
                </div>
                <Button className="w-full btn-primary" onClick={handleStake} disabled={isStaking} data-testid="stake-button">
                  <Lock className="mr-2" size={18} /> {isStaking ? '...' : 'Stake AIH'}
                </Button>
              </TabsContent>
              <TabsContent value="unstake" className="mt-6 space-y-4">
                <div>
                  <Label className="text-white/80">Amount to Unstake</Label>
                  <Input type="number" placeholder="0.00" value={unstakeAmount} onChange={(e) => setUnstakeAmount(e.target.value)} className="input-cosmic" data-testid="unstake-amount" />
                  <p className="text-xs text-white/40 mt-1">Staked: {(user?.staked_tokens || 0).toLocaleString()} AIH</p>
                </div>
                <Button className="w-full btn-secondary" onClick={handleUnstake} disabled={isUnstaking} data-testid="unstake-button">
                  <Unlock className="mr-2" size={18} /> {isUnstaking ? '...' : 'Unstake AIH'}
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          <div className="glass-panel p-6" data-testid="token-info-panel">
            <h3 className="font-unbounded text-xl font-bold text-white mb-6">Token Info</h3>
            {tokenInfo && (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">Name</span><span className="text-white">{tokenInfo.name}</span></div>
                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">Price</span><span className="text-green-400">${tokenInfo.price}</span></div>
                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">Network</span><span className="text-white">{tokenInfo.network}</span></div>
                <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">Backing</span><span className="text-[#F7931A]">{tokenInfo.backing}</span></div>
                <a href="https://allinhandtokens.com" target="_blank" rel="noopener noreferrer" className="btn-primary w-full flex items-center justify-center gap-2 mt-4" data-testid="buy-token-link">Buy AIH <ExternalLink size={16} /></a>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 glass-panel p-6" data-testid="deposit-addresses">
          <h3 className="font-unbounded text-xl font-bold text-white mb-2">Deposit Addresses</h3>
          <p className="text-white/60 text-sm mb-6">Send crypto to these addresses to purchase AIH tokens</p>
          
          <div className="space-y-4">
            {addresses.map((addr, i) => (
              <div key={i} className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-semibold ${networkColors[addr.icon] || 'text-white'}`}>{addr.network}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyAddress(addr.address, i)} className="text-white/60 hover:text-white">
                    {copiedAddr === i ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
                  </Button>
                </div>
                <p className="font-mono text-sm text-white/80 break-all">{addr.address}</p>
              </div>
            ))}
          </div>
        </div>

        {tokenInfo && tokenInfo.staking_tiers && (
          <div className="mt-8 glass-panel p-6" data-testid="staking-tiers">
            <h3 className="font-unbounded text-xl font-bold text-white mb-6">Staking Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-[#0A1628]/80 rounded-xl p-5 border border-white/5 text-center">
                <span className="tier-badge bronze mb-3 inline-block">Bronze</span>
                <p className="font-unbounded text-2xl font-bold text-[#D4AF37] mb-1">8%</p>
                <p className="text-xs text-white/60">Min. 1,000 AIH</p>
              </div>
              <div className="bg-[#0A1628]/80 rounded-xl p-5 border border-white/5 text-center">
                <span className="tier-badge silver mb-3 inline-block">Silver</span>
                <p className="font-unbounded text-2xl font-bold text-[#D4AF37] mb-1">12%</p>
                <p className="text-xs text-white/60">Min. 10,000 AIH</p>
              </div>
              <div className="bg-[#0A1628]/80 rounded-xl p-5 border border-white/5 text-center">
                <span className="tier-badge gold mb-3 inline-block">Gold</span>
                <p className="font-unbounded text-2xl font-bold text-[#D4AF37] mb-1">18%</p>
                <p className="text-xs text-white/60">Min. 50,000 AIH</p>
              </div>
              <div className="bg-[#0A1628]/80 rounded-xl p-5 border border-white/5 text-center">
                <span className="tier-badge platinum mb-3 inline-block">Platinum</span>
                <p className="font-unbounded text-2xl font-bold text-[#D4AF37] mb-1">25%</p>
                <p className="text-xs text-white/60">Min. 100,000 AIH</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletPage;
